/**
 * Selection action bar.
 *
 * Floats over the bottom of the table only while rows are selected. It is an
 * overlay rather than a row inserted above the table on purpose: inserting one
 * shifts every row down at the moment of the first click, which reads as the
 * list having changed under you.
 */
import { X } from 'lucide-react';

export function BulkBar({
  count,
  on_clear,
  children,
}: {
  count: number;
  on_clear: () => void;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <div
      className="pointer-events-none sticky bottom-3 z-sticky flex justify-center px-3"
      // Announced so a keyboard user who just selected with Space knows the
      // actions appeared, without focus being yanked into the bar.
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-pill bg-foreground/95 px-2 py-1.5 pl-3 text-background shadow-overlay animate-slide-up-fade backdrop-blur">
        <span className="text-data font-semibold tabular">
          {count} selected
        </span>
        <span aria-hidden="true" className="h-4 w-px bg-background/25" />
        {children}
        <button
          type="button"
          onClick={on_clear}
          className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-background/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background"
          aria-label="Clear selection"
          data-testid="bulk-clear"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/** Button styled for use inside the bulk bar (dark ground, light text). */
export const bulk_btn =
  'inline-flex h-7 items-center gap-1.5 rounded-pill bg-background/15 px-2.5 text-data font-semibold transition-colors hover:bg-background/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-3.5';

export const bulk_btn_danger =
  'inline-flex h-7 items-center gap-1.5 rounded-pill bg-destructive px-2.5 text-data font-semibold text-destructive-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-3.5';
